/**
 * Commission - the shared vocabulary, and the invoice split.
 *
 * This file used to hold the whole v1 engine: a provisional figure struck at Sold, a
 * percentage held back, a final figure struck at Settled, a true-up window, a materiality
 * threshold and a clawback. **Build spec v2, section 0, scraps all of it** - "that model is
 * scrapped. It was based on an assumption that turned out to contradict the accounting team's
 * actual Xero journal design." Commission is calculated once, at Sold, and never recalculated;
 * when an estimate turns out wrong the correction goes through the P&L, not through somebody's
 * pay. That model lives in `commissionV2.ts`, and it is the only one the platform runs on.
 *
 * So the v1 engine is gone rather than left sitting next to its replacement. Two of them in
 * the tree is how the wrong one gets imported a year from now.
 *
 * What is left is what other modules actually use, and each of it survives on its own merits:
 *
 *   - `CostType` and `COST_TYPE_LABEL` - the vocabulary for a cost line, shared by the
 *     Cost of Sales screen, the rate card and the report.
 *   - `money` - one rounding rule, applied where a figure becomes a figure rather than at the
 *     end of an accumulation, which is how a report stops agreeing with the sum of its rows.
 *   - `allocateInvoice` - spec section 5, unchanged from v1 and still required.
 *   - `Assumptions` / `isBillOverdue` - when a missing bill becomes old enough to accrue.
 *
 * `Assumptions` no longer carries `holdback_pct`, `true_up_window_days` or
 * `materiality_threshold`. Section 1 lists them under "Removed from v1", and with the engine
 * gone nothing could read them anyway.
 */

export type CostType =
  | 'PurchasePrice'
  | 'Inspection'
  | 'FreightIn'
  | 'Reconditioning'
  | 'FreightOut'
  | 'OtherOutflow'
  | 'Overhead'
  /* Winnie, 13 Sep: the lot load fee, auto-applied at Paid - Ready for Sale. Its own type
     rather than folded into PurchasePrice, because it is a holding charge on a car that is
     already bought - reading it as part of the purchase price would misstate both the
     acquisition cost and the margin scheme figure that keys off it. */
  | 'LotLoad'

export type SplitMethod = 'Equal' | 'Weighted' | 'Manual'

export const COST_TYPE_LABEL: Record<CostType, string> = {
  PurchasePrice: 'Purchase price',
  Inspection: 'Inspection',
  FreightIn: 'Freight in',
  Reconditioning: 'Reconditioning',
  FreightOut: 'Freight out',
  OtherOutflow: 'Other outflow',
  Overhead: 'Overhead',
  LotLoad: 'Lot load fee',
}

export type CostDirection = 'Inflow' | 'Outflow'

/**
 * Which side of the sale a cost sits on.
 *
 * Inflow is what it took to get the car in: the purchase itself, the inspection, the
 * freight from wherever the seller is, and any reconditioning done before it is offered.
 * Outflow is what the sale costs: the freight out to the buyer's dealership, and anything
 * else spent on the selling side. Nick, 10 Sep: "inflow purchase freight in is location of
 * the seller. outflow sales freight out is the buyer location (dealership)."
 *
 * Both sides are already inside landed cost, so this split changes no arithmetic - it only
 * lets the report show how the landed figure was built, which is what Winnie asked for.
 * Overhead maps to null rather than to a side: it is not a cost of this particular car, and
 * inventing a side for it would put a company expense on one half of a vehicle's margin.
 */
export const COST_DIRECTION: Record<CostType, CostDirection | null> = {
  PurchasePrice: 'Inflow',
  Inspection: 'Inflow',
  FreightIn: 'Inflow',
  Reconditioning: 'Inflow',
  FreightOut: 'Outflow',
  OtherOutflow: 'Outflow',
  Overhead: null,
  /* Inflow: it is spent getting the car ready to sell, alongside the inspection and the
     freight in, and it belongs in landed cost the same way they do. */
  LotLoad: 'Inflow',
}

/**
 * A cost line with no cost type is unclassified, not overhead, and not an inflow. It
 * returns null so a caller can group it under its own heading and say so out loud, rather
 * than quietly folding an unknown cost into one side of the margin.
 */
export function directionOf(costType: CostType | null | undefined): CostDirection | null {
  if (!costType) return null
  return COST_DIRECTION[costType] ?? null
}

/**
 * The two settings the v2 model still has an opinion about.
 *
 * `default_commission_pct` is the rate a vehicle is struck at when nothing more specific is
 * configured; `missing_bill_cutoff_days` is how long a cost waits for its invoice before it
 * is old enough to accrue at the standard rate.
 */
export interface Assumptions {
  default_commission_pct: number
  missing_bill_cutoff_days: number
}

export const DEFAULT_ASSUMPTIONS: Assumptions = {
  default_commission_pct: 0.2,
  missing_bill_cutoff_days: 7,
}

/**
 * Money is rounded once, at the point a figure becomes a figure - not accumulated at
 * full precision and rounded at the end, which is how a report stops agreeing with the
 * sum of its own rows.
 */
export function money(value: number | string | null | undefined): number {
  const n = Number(value)
  if (!Number.isFinite(n)) return 0
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export interface AllocationInput {
  invoiceTotal: number | string
  vins: string[]
  method?: SplitMethod
  amounts?: Record<string, number | string>
  weights?: Record<string, number | string>
}

export interface AllocationResult {
  ok: boolean
  reason: string | null
  method?: SplitMethod
  invoiceTotal?: number
  allocations: { vin: string; allocated_amount: number }[]
  allocatedTotal?: number
  variance: number
}

/**
 * Split one supplier invoice across several vehicles.
 *
 * The rule that matters is the last one: the allocations must add up to the invoice
 * total to the cent, or nothing posts. Equal splits are made to reconcile by putting
 * the rounding remainder on the final line rather than letting it evaporate - $100
 * across three cars is 33.33, 33.33, 33.34, not three lines that quietly lose a cent.
 */
export function allocateInvoice(input: AllocationInput): AllocationResult {
  const { invoiceTotal, vins, method } = input || ({} as AllocationInput)
  const total = money(invoiceTotal)
  const list = Array.isArray(vins) ? vins.slice() : []

  if (!list.length) {
    return { ok: false, reason: 'no_vehicles', allocations: [], variance: total }
  }
  if (!(total > 0)) {
    return { ok: false, reason: 'invoice_total_not_positive', allocations: [], variance: 0 }
  }

  let allocations: { vin: string; allocated_amount: number }[] = []

  if (method === 'Manual') {
    const amounts = input.amounts || {}
    allocations = list.map((vin) => ({ vin, allocated_amount: money(amounts[vin]) }))
  } else if (method === 'Weighted') {
    const weights = input.weights || {}
    let weightTotal = 0
    for (const vin of list) weightTotal += Number(weights[vin]) || 0
    if (!(weightTotal > 0)) {
      return { ok: false, reason: 'weights_not_positive', allocations: [], variance: total }
    }
    let running = 0
    allocations = list.map((vin, i) => {
      if (i === list.length - 1) return { vin, allocated_amount: money(total - running) }
      const amount = money(total * ((Number(weights[vin]) || 0) / weightTotal))
      running = money(running + amount)
      return { vin, allocated_amount: amount }
    })
  } else {
    const each = money(total / list.length)
    let running = 0
    allocations = list.map((vin, i) => {
      if (i === list.length - 1) return { vin, allocated_amount: money(total - running) }
      running = money(running + each)
      return { vin, allocated_amount: each }
    })
  }

  let allocated = 0
  for (const a of allocations) allocated = money(allocated + a.allocated_amount)
  const variance = money(allocated - total)

  return {
    ok: variance === 0,
    reason: variance === 0 ? null : 'does_not_reconcile',
    method: method || 'Equal',
    invoiceTotal: total,
    allocations,
    allocatedTotal: allocated,
    variance,
  }
}

/**
 * A cost with no invoice behind it, past the cutoff, becomes an estimate rather than a
 * silence. The spec is right that this is the riskier gap - nothing prompts anybody to
 * notice a bill that never arrived, so the vehicle simply looks cheaper than it was.
 */
export function isBillOverdue(
  dateIncurred: string | null | undefined,
  today: string,
  assumptions?: Partial<Assumptions> | null,
): boolean {
  const a: Assumptions = { ...DEFAULT_ASSUMPTIONS, ...assumptions }
  const d = new Date(String(dateIncurred))
  if (Number.isNaN(d.getTime())) return false
  d.setUTCDate(d.getUTCDate() + Number(a.missing_bill_cutoff_days))
  return String(today).slice(0, 10) > d.toISOString().slice(0, 10)
}
